import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { RootState, store } from '../..';
import { GroupSetting } from '@/models/school-group/group-setting';
import { SchoolGroupStudent } from '@/models/school-group/school-group-student';
import { SchoolGroup } from '@/models/school-group/school-group';
import { CompetencySetting } from '@/models/competency/competency-setting';
import { SchoolGroupCompetencyRanking } from '@/models/school-group/school-group-competency-ranking';
import { schoolGroupService } from '@/service/api/school-group.service';
import { StudentGroupingResult } from '@/models/student/student-grouping-result';
import { studengGroupingResultService } from '@/service/api/student-grouping-result.service';
import { AddSchoolGroupRequest } from '@/models/school-group/add-school-group-request';
import { SchoolGroupDetail } from '@/models/school-group/school-group-detail';
import { schoolGroupDetailService } from '@/service/api/school-group-detail.service';
import FileSaver from 'file-saver';
import { CompetencyRankingParams } from '@/models/competency/competency-ranking-params';
import { GroupModalProperty } from '@/models/school-group/group-modal-property';
import { SchoolGroupSearch } from '@/models/school-group/school-group-search';
import { SchoolGroupStudentSearch } from '@/models/school-group/school-group-student-search';
import { CompetencyRankingSettingRequest } from '@/models/competency/competency-ranking-setting-request';
import { SchoolGroupUpdateRequest } from '@/models/school-group/school-group-update-request';
import i18n from '@/i18n';

export interface IGroupState {
  groupSetting: GroupSetting; // グループ設定情報
  schoolGroups: SchoolGroup[]; // グループ情報一覧
  groupModalProperty: GroupModalProperty; // グループ設定モーダル制御情報
  competencySettings: CompetencySetting[]; // コンピテンシー設定情報一覧 //TODO 別モジュールで管理
  competencyRanking: SchoolGroupCompetencyRanking[]; //コンピテンシーランキング情報 //TODO 別モジュールで管理
  studentGroupingResults: StudentGroupingResult; // グルーピング結果
  studentGroups: SchoolGroupStudent[]; // グループ別生徒情報
  schoolGroupDetail: SchoolGroupDetail; // グループ詳細情報
}
@Module({ dynamic: true, store, name: 'groupModule', namespaced: true })
class GroupModule extends VuexModule implements IGroupState {
  groupSetting: GroupSetting = new GroupSetting();
  schoolGroups: SchoolGroup[] = [];
  groupModalProperty: GroupModalProperty = {
    isShowGroupsettingModal: false,
    isGroupSettingsDataSubmitted: false,
  };
  competencySettings: CompetencySetting[] = [];
  competencyRanking: SchoolGroupCompetencyRanking[] = [];
  studentGroupingResults: StudentGroupingResult = new StudentGroupingResult();
  studentGroups: SchoolGroupStudent[] = [];
  schoolGroupDetail: SchoolGroupDetail = new SchoolGroupDetail();

  get isFourStudentByGroup(): boolean {
    if (this.studentGroups.length > 0) {
      return this.studentGroups.every((group: SchoolGroupStudent) => {
        return group.studentList.length >= 3;
      });
    }
  }

  get competencyRankingByType(): SchoolGroupCompetencyRanking[] {
    return this.competencyRanking.sort(function (a, b): number {
      return a.competencyType > b.competencyType ? 1 : -1;
    });
  }

  @Mutation
  setGroupSetting(value: GroupSetting) {
    this.groupSetting = value;
  }

  @Mutation
  setSchoolGroups(value: SchoolGroup[]) {
    this.schoolGroups = value;
  }

  @Mutation
  setCompetencySettings(value: CompetencySetting[]) {
    this.competencySettings = value;
  }

  @Mutation
  setCompetencyRanking(value: SchoolGroupCompetencyRanking[]) {
    this.competencyRanking = value;
  }

  @Mutation
  cleanSchoolGroupSetting() {
    this.competencySettings = [];
  }

  @Mutation
  cleanCompetencyRanking() {
    this.competencyRanking = [];
  }

  @Mutation
  cleanSchoolGroups() {
    this.schoolGroups = [];
  }

  @Mutation
  setStudentGroupingResults(value: StudentGroupingResult) {
    this.studentGroupingResults = value;
  }

  @Mutation
  setStudentGroups(value: SchoolGroupStudent[]) {
    this.studentGroups = value;
  }

  @Mutation
  setSchoolGroupDetail(value: SchoolGroupDetail) {
    this.schoolGroupDetail = value;
  }

  @Mutation
  setSchoolGroupSettingInfo(schoolGroupSettingInfo: GroupModalProperty) {
    this.groupModalProperty = schoolGroupSettingInfo;
  }

  @Mutation
  clean() {
    this.studentGroupingResults = new StudentGroupingResult();
    this.studentGroups = [];
  }

  /**
   * グルーピングデータ取得
   * @param schoolGroupSearch グルーピング検索情報
   */
  @Action({ rawError: true })
  async fetchSchoolGroup(schoolGroupSearch: SchoolGroupSearch) {
    const result = await schoolGroupService.getSchoolGroups(schoolGroupSearch.targetType, schoolGroupSearch.targetID, schoolGroupSearch.selectedCommaTagIDs);
    this.setSchoolGroups(result);
  }

  /**
   * コンピテンシー設定取得
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchCompetencySetting(params: SchoolGroupSearch) {
    const result = await schoolGroupService.getCompetencySetting(params.targetType, params.targetID);
    this.setCompetencySettings(result);
  }

  /**
   * コンピテンシーランキング取得
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchCompetencyRanking(params: CompetencyRankingParams) {
    const result = await schoolGroupService.getCompetencyRanking(params.targetType, params.targetID, params.selectedCommaTagIDs);
    this.setCompetencyRanking(result);
  }

  /**
   * コンピテンシー設定更新
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async updateCompetencySetting(params: CompetencyRankingSettingRequest) {
    return await schoolGroupService.putCompetencySetting(params);
  }

  /**
   * グルーピング結果取得
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchStudentGroupingResults(schoolGroupStudentSearch: SchoolGroupStudentSearch) {
    // 学校コンピテンシーが指定された場合は育成目的は0で渡す
    if (schoolGroupStudentSearch.schoolCompetencyID !== 0) {
      schoolGroupStudentSearch.growingPurpose = 0;
    }
    const result = await studengGroupingResultService.getStudengGroupingResult(
      schoolGroupStudentSearch.targetType,
      schoolGroupStudentSearch.targetID,
      schoolGroupStudentSearch.selectedCommaTagIDs,
      schoolGroupStudentSearch.groupNum,
      schoolGroupStudentSearch.groupType,
      schoolGroupStudentSearch.growingPurpose,
      schoolGroupStudentSearch.schoolCompetencyID
    );
    this.setStudentGroupingResults(result);
  }

  /**
   * グループ情報登録
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  createSchoolGroup(params: AddSchoolGroupRequest) {
    return studengGroupingResultService.addSchoolGroup(params);
  }

  /**
   * グループ情報取得
   * @param groupID リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchSchoolGroupDetail(groupID: number) {
    const schoolGroupDetail = await schoolGroupDetailService.getSchoolGroupDetail(groupID);
    this.setSchoolGroupDetail(schoolGroupDetail);
  }

  /**
   * グループ情報更新
   * @param params リクエストパラメータ
   */
  @Action({ rawError: true })
  async updateSchoolGroup(params: SchoolGroupUpdateRequest) {
    return await schoolGroupDetailService.updateSchoolGroupDetail(params.groupID, params.data);
  }

  /**
   * グループ情報エクセルダウンロード
   * @param groupID グループID
   */
  @Action({ rawError: true })
  async downloadProgress(groupID: number) {
    const file = await schoolGroupDetailService.downloadProgress(groupID);
    FileSaver.saveAs(file, `リストをダウンロード.xlsx`);
  }

  /**
   * グループ削除
   * @param groupID グループID
   */
  @Action({ rawError: true })
  async deleteSchoolGroup(groupID: number) {
    const result = await schoolGroupDetailService.deleteSchoolGroup(groupID);
    return;
  }
}
export const groupModule = getModule(GroupModule);
